import { Menu, MenuButton, MenuItems, Transition } from '@headlessui/react';
import { useCallback, useRef } from 'react';

import AdjustmentsHorizontalIcon from '@heroicons/react/20/solid/AdjustmentsHorizontalIcon';
import ArrowLeftIcon from '@heroicons/react/20/solid/ArrowLeftIcon';
import BookOpenIcon from '@heroicons/react/20/solid/BookOpenIcon';
import ChartBarIcon from '@heroicons/react/20/solid/ChartBarIcon';
import ChatBubbleLeftEllipsisIcon from '@heroicons/react/20/solid/ChatBubbleLeftEllipsisIcon';
import PaintBrushIcon from '@heroicons/react/20/solid/PaintBrushIcon';
import UserIcon from '@heroicons/react/20/solid/UserIcon';
import UserPlusIcon from '@heroicons/react/20/solid/UserPlusIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import { startPersistentInfoToast, stopPersistentInfoToast } from '../../api/utils';
import toast from 'react-hot-toast';
import { tokenApi } from '../../api/service';
import { DROPDOWN_TRANSITION } from '../_misc/dropdown/constants';
import { DropdownOption } from '../_misc/dropdown/DropdownOption';
import { m } from 'framer-motion';
import { BUTTON_ANIMATION } from '../_misc/buttons/constants';

import classNames from 'classnames';
import { Button } from '../_misc/buttons/Button';
import { ButtonType } from '../_misc/buttons/ButtonType';
import { Image } from '../_misc/image/Image';
import { Ratio, Size } from '../../constants';
import { IconButton } from '../_misc/buttons/IconButton';
import { getUserDashboardUrl, getUserHomePageUrl } from '../user/user.constants';
import { useAuth } from '../../context/auth/useAuth';
import { usePurchase } from '../../context/purchase/usePurchase';
import { ThemePopup } from '../../context/theme/ThemeConstantsUpdated';
import { NAVBAR_MOUSE_TRACKING_EVENT } from '../../hooks/useNavbarOverride';
import { ShieldIcon } from 'lucide-react';

const { background, border, mainText, secondaryText, tertiaryText, shadow } = ThemePopup;

export const ProfileDropdown = (props: Props) => {
  const { isVibrant } = props;
  const { currentUser, clearUserInfo } = useAuth();
  const { isSubscribing, toggleSubscribing } = usePurchase();
  const navigate = useNavigate();
  const textClasses = classNames(tertiaryText, 'font-semibold mr-auto');
  const iconClasses = classNames(secondaryText, 'mr-3 h-5 w-5');
  const location = useLocation();
  const userProfileUrl = getUserDashboardUrl(currentUser!.id);
  const userHomePageUrl = getUserHomePageUrl(currentUser!);
  const isHomePage = location.pathname === userHomePageUrl;
  const isStorePage = location.pathname === '/store';
  const isProfilePage = location.pathname === userProfileUrl;
  const isAnalyticsPage = location.pathname === '/analytics';
  const isAdminPage = location.pathname === '/admin-panel';
  const isMember = currentUser?.badges.includes('Member');
  const isAdmin = currentUser?.roles.includes('Admin');
  const navigateToPage = (location: string, isAlreadyThere: boolean) => (isAlreadyThere ? null : navigate(location));

  const prevOpenRef = useRef(false);

  const logout = useCallback(
    async (close: () => void) => {
      const afterAttempt = () => {
        clearUserInfo();

        stopPersistentInfoToast(toastId);

        navigate('/login');
        toast.success('You have been successfully logged out.');
      };
      const toastId = startPersistentInfoToast(
        'Logging you out...',
        'Please wait while we log you out of your session...'
      );
      close();

      try {
        await tokenApi.logout();
        afterAttempt();
      } catch (error) {
        afterAttempt();
      }
    },
    [navigate, clearUserInfo]
  );

  const handleMouseTracking = (isEnabled: boolean) =>
    new CustomEvent(NAVBAR_MOUSE_TRACKING_EVENT, {
      detail: isEnabled,
      bubbles: true,
      cancelable: true,
    });

  return (
    <Menu as="div" className="relative flex justify-center align-middle">
      {({ close, open }) => (
        <>
          {(() => {
            if (open !== prevOpenRef.current) {
              window.dispatchEvent(handleMouseTracking(open));
              prevOpenRef.current = open;
            }
            return null;
          })()}
          <MenuButton className="flex items-center rounded-full">
            <m.div {...BUTTON_ANIMATION}>
              <Image
                size={Size.xs}
                ratio={Ratio.square}
                sizeClass="h-12 w-12"
                cornerStyle={Size.xl}
                imageId={currentUser ? { userImageId: currentUser.imageId } : {}}
                PlaceholderIcon={UserIcon}
                isVibrant={isVibrant}
                isSecondary
              />
            </m.div>
          </MenuButton>
          <Transition {...DROPDOWN_TRANSITION}>
            <MenuItems
              as="div"
              className={classNames(
                background,
                shadow,
                'absolute right-0 top-12 z-10 mt-3 w-[275px] origin-top-right rounded-md text-left focus:outline-none'
              )}
            >
              <div className="px-3 py-3">
                <h2 className={classNames(mainText, 'truncate text-xl font-semibold')}>{currentUser?.name}</h2>
                <p className={classNames(secondaryText, 'truncate text-sm')}>{currentUser?.email}</p>
              </div>
              <div className={classNames(border, 'border-y')}>
                <DropdownOption isActive={isHomePage} onClick={() => navigateToPage(userHomePageUrl, isHomePage)}>
                  <BookOpenIcon className={iconClasses} />
                  <span className={textClasses}>Your Homepage</span>
                </DropdownOption>
                <DropdownOption isActive={isProfilePage} onClick={() => navigateToPage(userProfileUrl, isProfilePage)}>
                  <AdjustmentsHorizontalIcon className={iconClasses} />
                  <span className={textClasses}>Profile Dashboard</span>
                </DropdownOption>
                <DropdownOption
                  isActive={isAnalyticsPage}
                  onClick={() => navigateToPage('/analytics', isAnalyticsPage)}
                >
                  <ChartBarIcon className={iconClasses} />
                  <span className={textClasses}>Analytics Dashboard</span>
                </DropdownOption>
                <DropdownOption isActive={isSubscribing} onClick={() => toggleSubscribing()}>
                  <UserPlusIcon className={iconClasses} />
                  <span className={textClasses}>{isMember ? 'Manage Membership' : 'Upgrade Account'}</span>
                </DropdownOption>
                <DropdownOption isActive={isStorePage} onClick={() => navigateToPage('/store', isStorePage)}>
                  <PaintBrushIcon className={iconClasses} />
                  <span className={textClasses}>Theme Store</span>
                </DropdownOption>
                {isAdmin && (
                  <DropdownOption isActive={isAdminPage} onClick={() => navigateToPage('/admin-panel', isAdminPage)}>
                    <ShieldIcon className={iconClasses} />
                    <span className={textClasses}>Admin Panel</span>
                  </DropdownOption>
                )}
              </div>
              <div className={classNames(mainText, 'flex flex-row justify-between rounded-b px-3 pb-4 pt-3')}>
                <Button onClick={() => logout(close)} Icon={ArrowLeftIcon}>
                  Logout
                </Button>
                <IconButton
                  Icon={ChatBubbleLeftEllipsisIcon}
                  buttonType={ButtonType.Alert}
                  onClick={() => navigate('/feedback')}
                />
              </div>
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};

type Props = {
  isVibrant?: boolean;
};
